"use client";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { intlLocale } from "@/i18n/config";
import { api, Guide, GuideSection } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { getToken } from "@/lib/auth";

// ── Auto-linking: turn mentions of other pages into links ────────────────────
const LINK_RULES: { href: string; phrases: string[] }[] = [
  { href: "/dashboard", phrases: ["Patient Cases", "Hasta Vakaları", "case library", "vaka kütüphanen", "vaka kütüphanesi"] },
  { href: "/settings", phrases: ["Settings", "Ayarlar"] },
  { href: "/create-patient", phrases: ["custom patients", "custom patient", "özel hasta", "Create Custom Patient"] },
];
const PHRASE_HREF: Record<string, string> = {};
LINK_RULES.forEach((r) => r.phrases.forEach((p) => { PHRASE_HREF[p.toLowerCase()] = r.href; }));
const escRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const LINK_RE = new RegExp(
  "(" + LINK_RULES.flatMap((r) => r.phrases).sort((a, b) => b.length - a.length).map(escRe).join("|") + ")",
  "gi"
);

function linkify(text: string, kp: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const matched = m[0];
    const href = PHRASE_HREF[matched.toLowerCase()];
    out.push(
      href ? (
        <Link
          key={`${kp}-${m.index}`}
          href={href}
          className="font-medium text-[var(--accent)] underline decoration-[var(--accent)]/40 underline-offset-2 hover:decoration-[var(--accent)] transition-colors"
        >
          {matched}
        </Link>
      ) : (
        matched
      )
    );
    last = m.index + matched.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// ── Section icon heuristic (by heading keywords) ─────────────────────────────
const ICON: Record<string, string> = {
  start: "M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z",
  users: "M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z",
  chat: "M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155",
  clipboard: "M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75",
  clock: "M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  sparkles: "M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z",
  card: "M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z",
  gear: "M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z",
  bulb: "M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18",
  book: "M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25",
};
function sectionIcon(heading: string): string {
  const h = heading.toLowerCase();
  const has = (...ks: string[]) => ks.some((k) => h.includes(k));
  if (has("başlar", "getting started", "sign in", "giriş", "kayıt", "account", "hesap")) return ICON.start;
  if (has("vaka", " case", "patient", "hasta", "library", "kütüphane")) return ICON.users;
  if (has("seans", "session", "therapy", "terapi", "görüşme")) return ICON.chat;
  if (has("rapor", "report", "feedback", "geri bildirim", "süpervizör", "supervisor")) return ICON.clipboard;
  if (has("geçmiş", "history")) return ICON.clock;
  if (has("özel", "custom", "oluştur", "create", "üret")) return ICON.sparkles;
  if (has("abonelik", "subscription", "plan")) return ICON.card;
  if (has("ayar", "settings", "dil", "language", "görünüm", "appearance", "api")) return ICON.gear;
  if (has("ipuc", "ipuç", "tips", "pratik", "practice")) return ICON.bulb;
  return ICON.book;
}

export default function GuidePage() {
  const router = useRouter();
  const t = useTranslations("guide");
  const locale = useLocale();
  const [guide, setGuide] = useState<Guide | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!getToken()) { router.push("/login"); return; }
    api.getGuide(locale).then(setGuide).catch(() => {}).finally(() => setLoading(false));
  }, [router, locale]);

  const content = guide?.content;

  const filtered = useMemo(() => {
    if (!content) return [] as { s: GuideSection; i: number }[];
    const all = content.sections.map((s, i) => ({ s, i }));
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(({ s }) =>
      s.heading.toLowerCase().includes(q) ||
      s.body.toLowerCase().includes(q) ||
      s.bullets.some((b) => b.toLowerCase().includes(q))
    );
  }, [content, query]);

  const updated = guide?.generated_at
    ? new Date(guide.generated_at).toLocaleDateString(intlLocale(locale), {
        year: "numeric", month: "long", day: "numeric",
      })
    : null;

  const num = (i: number) => String(i + 1).padStart(2, "0");

  return (
    <AppShell active="guide">
      <main className="max-w-3xl mx-auto px-5 py-12">
        {loading || !content ? (
          <div className="flex items-center justify-center py-24">
            <span className="text-[var(--text-muted)] text-sm">{t("loading")}</span>
          </div>
        ) : (
          <>
            {/* Hero */}
            <div
              className="mb-7 rounded-2xl border border-[var(--border)] p-6 md:p-8"
              style={{ background: "linear-gradient(135deg, var(--accent-ghost), var(--surface) 70%)" }}
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[var(--accent-light)] text-[var(--accent)] flex items-center justify-center shrink-0">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d={ICON.book} />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h1 className="text-3xl font-semibold text-[var(--text-primary)] leading-tight" style={{ fontFamily: "var(--font-lora)" }}>
                    {content.title}
                  </h1>
                  {updated && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[var(--surface-alt)] border border-[var(--border)] px-3 py-1 text-xs text-[var(--text-muted)]">
                      <svg className="w-3.5 h-3.5 text-[var(--accent)]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                      </svg>
                      {t("updated")} {updated} · {t("autoNote")}
                    </div>
                  )}
                </div>
              </div>
              {content.intro && (
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed mt-4">{linkify(content.intro, "intro")}</p>
              )}
            </div>

            {/* Search */}
            <div className="relative mb-6">
              <svg className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("searchPlaceholder")}
                className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] pl-10 pr-4 py-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)] focus:bg-[var(--surface)] transition-colors"
              />
            </div>

            {/* Table of contents */}
            {filtered.length > 1 && (
              <div className="mb-6">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-2.5">{t("contents")}</p>
                <div className="flex flex-wrap gap-2">
                  {filtered.map(({ s, i }) => (
                    <a
                      key={i}
                      href={`#s-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
                    >
                      <span className="text-[var(--text-muted)] font-semibold">{num(i)}</span>
                      {s.heading}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Sections */}
            {filtered.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-xl p-6 text-center">
                {t("noResults")}
              </p>
            ) : (
              <div className="space-y-4">
                {filtered.map(({ s, i }) => (
                  <section id={`s-${i}`} key={i} className="scroll-mt-6 bg-[var(--surface)] rounded-2xl border border-[var(--border)] p-6">
                    <div className="flex items-start gap-3.5">
                      <div className="w-10 h-10 rounded-xl bg-[var(--accent-light)] text-[var(--accent)] flex items-center justify-center shrink-0">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d={sectionIcon(s.heading)} />
                        </svg>
                      </div>
                      <div className="min-w-0 pt-0.5">
                        <div className="text-[11px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">{num(i)}</div>
                        <h2 className="text-lg font-semibold text-[var(--text-primary)] leading-snug" style={{ fontFamily: "var(--font-lora)" }}>
                          {s.heading}
                        </h2>
                      </div>
                    </div>
                    <div className="sm:pl-[54px] mt-2">
                      {s.body && <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{linkify(s.body, `b-${i}`)}</p>}
                      {s.bullets.length > 0 && (
                        <ul className="mt-3 space-y-2">
                          {s.bullets.map((b, j) => (
                            <li key={j} className="flex gap-2.5 text-sm text-[var(--text-secondary)] leading-relaxed">
                              <svg className="w-4 h-4 mt-0.5 shrink-0 text-[var(--accent)]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                              </svg>
                              <span>{linkify(b, `l-${i}-${j}`)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </section>
                ))}
              </div>
            )}

            {content.footer && (
              <div className="mt-8 rounded-2xl border border-[var(--border)] bg-[var(--accent-ghost)] p-5">
                <p className="text-xs text-[var(--text-muted)] italic leading-relaxed">{linkify(content.footer, "footer")}</p>
              </div>
            )}
          </>
        )}
      </main>
    </AppShell>
  );
}
